import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, ArrowLeft, Users, Send, FileText } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';
import { getErrorMessage } from '../utils/apiError';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY = {
  full_name: '',
  nric_fin_last4: '',
  date_of_birth: '',
  nationality: '',
  designation: '',
  contact_no: '',
  work_permit_no: '',
  work_permit_expiry: '',
  safety_cert_no: '',
  safety_cert_type: '',
  safety_cert_expiry: '',
  date_joined: '',
  photo: '',
  status: 'active',
  notes: '',
  lorry_number: '',
  iu_number: '',
};

const EMPTY_SUBMISSION = {
  customer_email: '',
  customer_name: '',
  customer_company: '',
  project_name: '',
  cover_message: '',
};

export const EmployeeManagement = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...EMPTY });
  const [showSubmit, setShowSubmit] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [submission, setSubmission] = useState({ ...EMPTY_SUBMISSION });
  const [submitting, setSubmitting] = useState(false);

  const fetch = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/employees`);
      setItems(res.data || []);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to load employees'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetch(); }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...EMPTY });
    setShowModal(true);
  };

  const openEdit = (it) => {
    setEditingId(it.id);
    setForm({ ...EMPTY, ...it });
    setShowModal(true);
  };

  const handlePhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Photo too large (max 2MB)');
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => setForm((f) => ({ ...f, photo: ev.target.result }));
    reader.readAsDataURL(file);
  };

  const save = async () => {
    if (!form.full_name || !form.designation) {
      toast.error('Full name and designation are required');
      return;
    }
    try {
      if (editingId) {
        await axios.put(`${BACKEND_URL}/api/employees/${editingId}`, form);
        toast.success('Employee updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/employees`, form);
        toast.success('Employee added');
      }
      setShowModal(false);
      fetch();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Save failed'));
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this employee record?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/employees/${id}`);
      toast.success('Deleted');
      fetch();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Delete failed'));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const openSubmit = () => {
    if (selectedIds.length === 0) {
      toast.error('Select at least one employee first (tick the checkboxes)');
      return;
    }
    setSubmission({ ...EMPTY_SUBMISSION });
    setShowSubmit(true);
  };

  const doSubmit = async () => {
    if (!submission.customer_email || !submission.customer_name) {
      toast.error('Customer name and email are required');
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...submission, employee_ids: selectedIds };
      const res = await axios.post(`${BACKEND_URL}/api/employees/submission`, payload);
      toast.success(`Submission sent — ${res.data.employee_count} employees emailed to ${submission.customer_email}`);
      setShowSubmit(false);
      setSelectedIds([]);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Submission failed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-40 pb-12">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate('/admin/billing')} data-testid="back-btn">
              <ArrowLeft size={20} />
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2"><Users /> Employee Records</h1>
              <p className="text-gray-600">Manage particulars + send Employee Submission Forms to customers</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={openSubmit} disabled={selectedIds.length === 0} data-testid="submit-btn">
              <Send size={16} className="mr-2" /> Send Submission ({selectedIds.length})
            </Button>
            <Button onClick={openCreate} data-testid="add-employee-btn">
              <Plus size={18} className="mr-2" /> Add Employee
            </Button>
          </div>
        </div>

        <Card>
          <CardHeader><CardTitle>All Employees</CardTitle></CardHeader>
          <CardContent>
            {loading ? <p className="text-gray-500">Loading...</p>
              : items.length === 0 ? (
                <div className="text-center py-12 text-gray-500">No employees yet. Click &quot;Add Employee&quot; to start.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="p-3 w-8"></th>
                        <th className="text-left p-3">Photo</th>
                        <th className="text-left p-3">Name</th>
                        <th className="text-left p-3">Designation</th>
                        <th className="text-left p-3">Contact</th>
                        <th className="text-left p-3">WP Expiry</th>
                        <th className="text-left p-3">Safety Cert</th>
                        <th className="text-left p-3">Status</th>
                        <th className="text-right p-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((it) => (
                        <tr key={it.id} className="border-b hover:bg-gray-50" data-testid={`employee-row-${it.id}`}>
                          <td className="p-3">
                            <Checkbox checked={selectedIds.includes(it.id)} onCheckedChange={() => toggleSelect(it.id)}
                                      data-testid={`select-${it.id}`} />
                          </td>
                          <td className="p-3">
                            {it.photo ? (
                              <img src={it.photo} alt="" className="w-10 h-10 rounded-full object-cover" />
                            ) : <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-xs text-gray-500">N/A</div>}
                          </td>
                          <td className="p-3 font-medium">{it.full_name}</td>
                          <td className="p-3">{it.designation}</td>
                          <td className="p-3">{it.contact_no || '-'}</td>
                          <td className="p-3">{it.work_permit_expiry || '-'}</td>
                          <td className="p-3">
                            {it.safety_cert_type ? <Badge variant="outline">{it.safety_cert_type}</Badge> : '-'}
                          </td>
                          <td className="p-3">
                            <Badge className={it.status === 'active' ? 'bg-green-600' : 'bg-gray-500'}>{it.status}</Badge>
                          </td>
                          <td className="p-3 text-right">
                            <div className="flex gap-1 justify-end">
                              <Button
                                size="sm"
                                variant="outline"
                                title="Download Particulars PDF"
                                onClick={() => window.open(`${BACKEND_URL}/api/employees/${it.id}/particulars-pdf`, '_blank')}
                                data-testid={`particulars-pdf-${it.id}`}
                              >
                                <FileText size={14} />
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => openEdit(it)}><Edit size={14} /></Button>
                              <Button size="sm" variant="outline" onClick={() => remove(it.id)}><Trash2 size={14} className="text-red-600" /></Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
          </CardContent>
        </Card>

        {/* Employee add/edit modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="employee-modal">
            <DialogHeader>
              <DialogTitle>{editingId ? 'Edit Employee' : 'Add Employee'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Full Name *</Label>
                  <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                         data-testid="form-full-name" />
                </div>
                <div>
                  <Label>Designation *</Label>
                  <Input value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })}
                         placeholder="e.g. Lorry Crane Operator" data-testid="form-designation" />
                </div>
                <div>
                  <Label>NRIC/FIN (last 4 only)</Label>
                  <Input value={form.nric_fin_last4} onChange={(e) => setForm({ ...form, nric_fin_last4: e.target.value })}
                         maxLength={5} placeholder="e.g. 567A" />
                </div>
                <div>
                  <Label>Date of Birth</Label>
                  <Input type="date" value={form.date_of_birth}
                         onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} />
                </div>
                <div>
                  <Label>Contact No.</Label>
                  <Input value={form.contact_no} onChange={(e) => setForm({ ...form, contact_no: e.target.value })} />
                </div>
                <div>
                  <Label>Nationality</Label>
                  <Input value={form.nationality} onChange={(e) => setForm({ ...form, nationality: e.target.value })}
                         placeholder="e.g. Singaporean" data-testid="form-nationality" />
                </div>
                <div>
                  <Label>Lorry Number</Label>
                  <Input value={form.lorry_number} onChange={(e) => setForm({ ...form, lorry_number: e.target.value })}
                         placeholder="e.g. XE1234A" data-testid="form-lorry-number" />
                </div>
                <div>
                  <Label>IU Number</Label>
                  <Input value={form.iu_number} onChange={(e) => setForm({ ...form, iu_number: e.target.value })}
                         placeholder="In-Vehicle Unit no." data-testid="form-iu-number" />
                </div>
                <div>
                  <Label>Date Joined</Label>
                  <Input type="date" value={form.date_joined}
                         onChange={(e) => setForm({ ...form, date_joined: e.target.value })} />
                </div>
                <div>
                  <Label>Work Permit / EP No.</Label>
                  <Input value={form.work_permit_no} onChange={(e) => setForm({ ...form, work_permit_no: e.target.value })} />
                </div>
                <div>
                  <Label>WP Expiry</Label>
                  <Input type="date" value={form.work_permit_expiry}
                         onChange={(e) => setForm({ ...form, work_permit_expiry: e.target.value })} />
                </div>
                <div>
                  <Label>Safety Cert Type</Label>
                  <Input value={form.safety_cert_type} onChange={(e) => setForm({ ...form, safety_cert_type: e.target.value })}
                         placeholder="e.g. CSOC / WAH / Rigger" />
                </div>
                <div>
                  <Label>Safety Cert No.</Label>
                  <Input value={form.safety_cert_no} onChange={(e) => setForm({ ...form, safety_cert_no: e.target.value })} />
                </div>
                <div>
                  <Label>Cert Expiry</Label>
                  <Input type="date" value={form.safety_cert_expiry}
                         onChange={(e) => setForm({ ...form, safety_cert_expiry: e.target.value })} />
                </div>
                <div>
                  <Label>Status</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Photo (max 2MB)</Label>
                <Input type="file" accept="image/*" onChange={handlePhoto} data-testid="form-photo" />
                {form.photo && <img src={form.photo} alt="" className="mt-2 w-24 h-24 rounded object-cover" />}
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
              <Button onClick={save} data-testid="form-save">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Employee Submission modal */}
        <Dialog open={showSubmit} onOpenChange={setShowSubmit}>
          <DialogContent className="max-w-xl" data-testid="submission-modal">
            <DialogHeader>
              <DialogTitle>Send Employee Submission Form</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="bg-blue-50 p-3 rounded text-sm">
                <FileText className="inline w-4 h-4 mr-1 text-blue-600" />
                Generating PDF for <strong>{selectedIds.length}</strong> selected employee{selectedIds.length !== 1 && 's'} and emailing to the customer.
              </div>
              <div>
                <Label>Customer Name *</Label>
                <Input value={submission.customer_name}
                       onChange={(e) => setSubmission({ ...submission, customer_name: e.target.value })}
                       data-testid="sub-customer-name" />
              </div>
              <div>
                <Label>Customer Email *</Label>
                <Input type="email" value={submission.customer_email}
                       onChange={(e) => setSubmission({ ...submission, customer_email: e.target.value })}
                       data-testid="sub-customer-email" />
              </div>
              <div>
                <Label>Customer Company</Label>
                <Input value={submission.customer_company}
                       onChange={(e) => setSubmission({ ...submission, customer_company: e.target.value })} />
              </div>
              <div>
                <Label>Project Name</Label>
                <Input value={submission.project_name}
                       onChange={(e) => setSubmission({ ...submission, project_name: e.target.value })}
                       placeholder="e.g. Site Alpha - Block 12" />
              </div>
              <div>
                <Label>Cover Message (optional)</Label>
                <Textarea value={submission.cover_message}
                          onChange={(e) => setSubmission({ ...submission, cover_message: e.target.value })}
                          rows={3} placeholder="Optional note to include in the email body" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSubmit(false)} disabled={submitting}>Cancel</Button>
              <Button onClick={doSubmit} disabled={submitting} data-testid="sub-send">
                {submitting ? 'Sending...' : 'Send PDF to Customer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default EmployeeManagement;
